/**
 * The admin stats dashboard's index of SimTeach puzzle sessions (issue
 * #4675): one small row per attempt at
 * `course/{courseId}/simTeachPuzzleSessions/{sessionId}`, so the dashboard
 * reads one course collection instead of every user's attempts. Written only
 * by simteach at session teardown (`build_session_index_row` in
 * `src/apps/simteach/app/section_teaching/puzzle_attempt.py`).
 */
import {z} from 'zod';

import {FirestoreTimestampSchema} from '../firebase/types';
import {SimTeachPuzzleKindSchema} from './simTeachPuzzle';
import {SimTeachPuzzleOutcomeSchema} from './simTeachPuzzleProgress';

/** The course subcollection holding one row per puzzle session. */
export const SIMTEACH_PUZZLE_SESSIONS_COLLECTION = 'simTeachPuzzleSessions';

/** One puzzle session's stats row. */
export const SimTeachPuzzleSessionIndexSchema = z.object({
  /** The player's uid; used only to count distinct players. */
  uid: z.string(),
  /** The course the puzzle belongs to. */
  courseId: z.string(),
  /** The puzzle's id. */
  puzzleId: z.string(),
  /** Guided puzzle (`lesson`) or puzzle. */
  kind: SimTeachPuzzleKindSchema,
  /** When the session started. */
  startedAt: FirestoreTimestampSchema,
  /** When it ended. */
  endedAt: FirestoreTimestampSchema,
  /** `endedAt - startedAt`, in seconds. */
  durationSec: z.number().nonnegative(),
  /** How it ended. */
  outcome: SimTeachPuzzleOutcomeSchema,
  /** The misstep that failed a puzzle's goal; null otherwise. */
  misstep: z.string().nullable(),
  /** Live teacher lines (the opening's replayed lines left out). */
  teacherTurns: z.number().int().nonnegative(),
  /** The player's course role when the session was saved; "unknown" if unread. */
  role: z.string(),
  /** Whether the session was a retry from an earlier attempt. Omitted: no. */
  resumed: z.boolean().optional(),
  /**
   * Where the section leader opened the puzzle from (the webapp's
   * `PuzzleEntrySource`, e.g. `playlist`). Null or omitted: not recorded.
   */
  entrySource: z.string().nullable().optional(),
});

/** One puzzle session's stats row. */
export type SimTeachPuzzleSessionIndex = z.infer<
  typeof SimTeachPuzzleSessionIndexSchema
>;

/**
 * The course collection of puzzle session rows.
 *
 * @param params.courseId - The course.
 * @return `course/{courseId}/simTeachPuzzleSessions`.
 * @throws Never.
 */
export function simTeachPuzzleSessionsCollectionPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `course/${courseId}/${SIMTEACH_PUZZLE_SESSIONS_COLLECTION}`;
}
